import React, { useMemo, useState } from 'react';
import DomainAuditCard from '../../components/ui/DomainAuditCard';
import WaybackTimelineCard from '../../components/WaybackTimelineCard';
import ComplianceAlertCard from '../../components/ComplianceAlertCard';
import EmailInfrastructureSection from '../../components/EmailInfrastructureSection';
import FeedResultCard from '../../components/FeedResultCard';
import CorporateBreachesCard from './CorporateBreachesCard';
import LegalCourtsCard from './LegalCourtsCard';
import DarkWebAdvisoryCard from '../../components/DarkWebAdvisoryCard';
import KeyPersonsSection from './KeyPersonsSection';
import CorporateFeedFilters from './CorporateFeedFilters';
import BreachesRiskSection from './BreachesRiskSection';
import CertificateTransparencyCard from './CertificateTransparencyCard';
import { useFeedInspect } from '../../components/FeedInspectProvider';
import { FEED_CHIP, DD_SECTION_DESC, DD_SECTION_TITLE } from '../../components/investigation/feedCardStyles';

function isNativeCorporateInfraRow(res) {
  if (!res) return false;
  if (res.source === 'corp_infra_probe') return true;
  return String(res.platform || '').toLowerCase() === 'corporate infrastructure';
}

function isWhoisIntelRow(res) {
  return res?.ui_bucket === 'domain_intel' && String(res.platform || '') === 'WHOIS Intel';
}

function isCertTransparencyRow(res) {
  if (String(res?.type || '') === 'corporate_infrastructure') return true;
  if (res?.ui_bucket !== 'domain_intel') return false;
  const p = String(res.platform || '').toLowerCase();
  return p === 'certspotter' || p === 'crt.sh' || p === 'domain infrastructure';
}

function isDomainSecurityAuditRow(res) {
  return res?.ui_bucket === 'cyber_trust_score';
}

function isThreatIntelVTRow(res) {
  return res?.ui_bucket === 'threat_intelligence';
}

function isKeyPersonRow(res) {
  return res?.ui_bucket === 'relationship_node';
}

function isUboRow(res) {
  return res?.ui_bucket === 'ubo_network';
}

function isCorporatePressRow(res) {
  return res?.ui_bucket === 'corporate_press';
}

function isSentimentRow(res) {
  return res?.ui_bucket === 'sentiment_analysis';
}

function isAdverseMediaRow(res) {
  return res?.ui_bucket === 'negative_news';
}

function isDedicatedDdBucket(res) {
  return (
    isKeyPersonRow(res) ||
    isUboRow(res) ||
    isCorporatePressRow(res) ||
    isSentimentRow(res) ||
    isAdverseMediaRow(res)
  );
}

function isLegalRiskRow(res) {
  return res?.ui_bucket === 'legal_risk';
}

function CorporateDdSection({
  title,
  description,
  borderClass,
  titleClass,
  children,
  hasRows,
  loading,
  scanComplete,
  hideWhenEmpty = false,
  sectionId = '',
}) {
  if (hideWhenEmpty && scanComplete && !loading && !hasRows) return null;
  if (!hasRows && !loading && !scanComplete) return null;

  return (
    <section
      id={sectionId || undefined}
      data-corp-section={sectionId || undefined}
      className={`scroll-mt-20 min-w-0 space-y-3 overflow-x-clip rounded-2xl border p-3 shadow-sm sm:p-4 ${borderClass}`}
    >
      <div>
        <h3 className={`text-[10px] font-black uppercase tracking-[0.2em] ${titleClass}`}>{title}</h3>
        <p className={DD_SECTION_DESC}>{description}</p>
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function isComplianceRow(res) {
  return res?.ui_bucket === 'compliance_alert';
}

function isCorporateBreachesRow(res) {
  return res?.ui_bucket === 'corporate_breaches';
}

function isDarkWebAdvisoryRow(res) {
  return res?.ui_bucket === 'darkweb_advisory';
}

function isWaybackRow(res) {
  return res?.ui_bucket === 'wayback_archive';
}

function showSection(activeFilter, sectionId) {
  return activeFilter === 'all' || activeFilter === sectionId;
}

const CORP_SECTION_DOM_IDS = {
  infrastructure: 'corp-section-infrastructure',
  legal: 'corp-section-legal',
  breaches_risk: 'corp-section-breaches_risk',
  key_persons: 'corp-section-key_persons',
  web_press: 'corp-section-web_press',
  reviews: 'corp-section-reviews',
};

function scrollToCorporateSection(filterId) {
  const domId = CORP_SECTION_DOM_IDS[filterId];
  if (!domId || typeof document === 'undefined') return;
  window.requestAnimationFrame(() => {
    const el = document.getElementById(domId);
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });
}

/** Same card layout as the main Due Diligence feed (DomainAuditCard + default institutional card). */
function renderCorporateFeedRow(res, reactKey, getDisplayName, { onPivotToPerson, companyName, companyDomain, locationTags } = {}) {
  if (res?.type === 'cache_meta') return null;
  const name = getDisplayName(res);
  if (!name && res?.ui_bucket !== 'executive_summary' && res?.ui_bucket !== 'darkweb_advisory') {
    return null;
  }

  const bucket = res.ui_bucket;

  if (bucket === 'cyber_trust_score' || bucket === 'threat_intelligence') {
    return <DomainAuditCard key={reactKey} data={res} idx={reactKey} />;
  }

  if (bucket === 'wayback_archive') {
    return <WaybackTimelineCard key={reactKey} data={res} idx={reactKey} />;
  }

  if (isCertTransparencyRow(res)) {
    return (
      <CertificateTransparencyCard
        key={reactKey}
        res={res}
        title={res.title || name || 'Certificate Transparency'}
      />
    );
  }

  if (bucket === 'executive_summary') {
    const summary = res.summary || {};
    const flags = Array.isArray(summary.key_flags) ? summary.key_flags : [];
    const rawSnippet = String(res.snippet || '');
    const safeSnippet = rawSnippet.replace(/^Overall risk\s+\w+\s*·\s*/i, '').trim();
    const cardRes = safeSnippet !== rawSnippet ? { ...res, snippet: safeSnippet } : res;
    return (
      <FeedResultCard
        key={reactKey}
        res={cardRes}
        title={res.title || 'Executive summary'}
        icon="📋"
      >
        <span className={FEED_CHIP}>AML {summary.aml_status || 'CLEAN'}</span>
        <span className={FEED_CHIP}>
          Breaches {Number(summary.breaches_found) || 0}
        </span>
        <span className={FEED_CHIP}>
          Adverse {Number(summary.adverse_signals_count) || 0}
        </span>
        <span className={`${FEED_CHIP} border-sky-500/30 text-sky-200`}>
          {summary.commercial_presence || 'UNKNOWN'}
        </span>
        {flags.map((flag) => (
          <span key={flag} className={`${FEED_CHIP} border-amber-500/30 text-amber-100`}>
            {flag}
          </span>
        ))}
      </FeedResultCard>
    );
  }

  const isAlert =
    (res.risk_score && res.risk_score > 50) ||
    res.risk_level === 'CRITICAL' ||
    res.error ||
    bucket === 'negative_news' ||
    bucket === 'legal_risk';

  const alertShellClass =
    'border-red-500/40 bg-red-950/30 hover:border-red-400/60 shadow-[0_0_10px_rgba(239,68,68,0.08)]';

  const pivotPerson =
    bucket === 'relationship_node'
      ? String(res.target_entity || res.person_focus || '').trim()
      : String(res.details?.person_focus || res.person_focus || '').trim();
  const pivotCompany =
    String(companyName || res.details?.primary_brand || '').trim() ||
    String(companyDomain || res.details?.domain || '').trim();
  const pivotHeaderAction =
    typeof onPivotToPerson === 'function' &&
    bucket === 'corporate_context' &&
    pivotPerson ? (
      <button
        type="button"
        data-testid="corporate-context-pivot-person"
        className="inline-flex min-h-[36px] w-full items-center justify-center gap-1 rounded-lg border border-cyan-500/30 bg-cyan-500/10 px-3 py-1.5 text-xs font-semibold text-cyan-300 transition hover:bg-cyan-500/20 sm:w-auto"
        onClick={() =>
          onPivotToPerson({
            personName: pivotPerson,
            companyName: pivotCompany,
            domain: String(companyDomain || res.details?.domain || '').trim(),
            locations: Array.isArray(locationTags) ? locationTags : [],
          })
        }
      >
        <span aria-hidden>👤</span>
        Pivot to Person Search
      </button>
    ) : null;

  switch (bucket) {
    case 'compliance_alert':
      return <ComplianceAlertCard key={reactKey} res={res} reactKey={reactKey} />;
    case 'negative_news': {
      return (
        <FeedResultCard
          key={reactKey}
          res={res}
          title={res.title || name}
          icon="📰"
          isAlert={isAlert}
          shellClassName={isAlert ? alertShellClass : ''}
        >
          {res.confidence ? (
            <span
              className={`${FEED_CHIP} ${
                res.confidence === 'High'
                  ? 'border-red-500/30 text-red-200'
                  : res.confidence === 'Medium'
                    ? 'border-amber-500/30 text-amber-200'
                    : ''
              }`}
            >
              {res.confidence} Match
            </span>
          ) : null}
        </FeedResultCard>
      );
    }
    case 'corporate_press': {
      return (
        <FeedResultCard
          key={reactKey}
          res={res}
          title={res.title || name}
          icon="📣"
        >
          {res.confidence ? (
            <span className={`${FEED_CHIP} border-emerald-500/30 text-emerald-200`}>
              {res.confidence} Mention
            </span>
          ) : null}
        </FeedResultCard>
      );
    }
    case 'corporate_context': {
      const industries = res.details?.industry_display || [];
      const locations = res.details?.location_hints || [];
      const brand =
        String(res.details?.primary_brand || companyName || '').trim() ||
        String(companyDomain || res.details?.domain || '').trim();
      const unifiedTitle = brand
        ? `Corporate Search Context — ${brand}`
        : String(res.title || name || 'Corporate Search Context').trim();
      return (
        <FeedResultCard
          key={reactKey}
          res={res}
          title={unifiedTitle}
          icon="🎯"
          headerActions={pivotHeaderAction}
        >
          {industries.length ? (
            <span className={`${FEED_CHIP} border-violet-500/30 text-violet-200`}>
              {industries[0]}
            </span>
          ) : null}
          {brand ? <span className={FEED_CHIP}>{brand}</span> : null}
          {locations.length ? (
            <span className={`${FEED_CHIP} border-sky-500/30 text-sky-200`}>
              {locations.join(', ')}
            </span>
          ) : null}
        </FeedResultCard>
      );
    }
    case 'corporate_breaches':
      return <CorporateBreachesCard key={reactKey} res={res} reactKey={reactKey} />;
    case 'darkweb_advisory':
      return <DarkWebAdvisoryCard key={reactKey} data={res} />;
    case 'sentiment_analysis': {
      return (
        <FeedResultCard
          key={reactKey}
          res={res}
          title={res.title || name}
          icon="🗣️"
        />
      );
    }
    case 'ubo_network': {
      return (
        <FeedResultCard
          key={reactKey}
          res={res}
          title={res.title || name}
          icon="🕸️"
        >
          {res.mention_context ? (
            <span className={`${FEED_CHIP} border-sky-500/30 text-sky-200`}>
              Brand mention
            </span>
          ) : null}
        </FeedResultCard>
      );
    }
    default: {
      const avatarUrl = res.details?.photo_url || res.details?.avatar_url;
      return (
        <FeedResultCard
          key={reactKey}
          res={res}
          title={name}
          icon={res.type === 'infrastructure' ? '📡' : '🏢'}
          avatarUrl={avatarUrl}
        />
      );
    }
  }
}

const CorporateDueDiligence = ({
  displayResults,
  loading,
  getDisplayName,
  scanComplete = false,
  companyName = '',
  companyDomain = '',
  locationTags = [],
  onPivotToPerson = null,
}) => {
  const { openInspect } = useFeedInspect();
  const rowOpts = { onPivotToPerson, companyName, companyDomain, locationTags };
  const [activeFilter, setActiveFilter] = useState('all');

  const handleFilterChange = (filterId) => {
    setActiveFilter(filterId);
    if (filterId && filterId !== 'all') {
      scrollToCorporateSection(filterId);
    }
  };

  const legalRecords = useMemo(
    () => (displayResults || []).filter(isLegalRiskRow),
    [displayResults],
  );

  const showLegalCourtsSection = loading || scanComplete;

  const nativeInfraRows = useMemo(
    () => (displayResults || []).filter(isNativeCorporateInfraRow),
    [displayResults],
  );

  const digitalFootprintOrdered = useMemo(() => {
    const list = (displayResults || []).filter((r) => !isNativeCorporateInfraRow(r));
    return [
      ...list.filter(isWhoisIntelRow),
      ...list.filter(isCertTransparencyRow),
      // Domain Security + VirusTotal render under Breaches & Risk.
    ];
  }, [displayResults]);

  const hasDigitalFootprint = nativeInfraRows.length > 0 || digitalFootprintOrdered.length > 0;

  const complianceRows = useMemo(
    () => (displayResults || []).filter(isComplianceRow),
    [displayResults],
  );

  const corporateBreachesRows = useMemo(
    () => (displayResults || []).filter(isCorporateBreachesRow),
    [displayResults],
  );

  const darkWebRows = useMemo(
    () => (displayResults || []).filter(isDarkWebAdvisoryRow),
    [displayResults],
  );

  const domainSecurityRows = useMemo(
    () => (displayResults || []).filter(isDomainSecurityAuditRow),
    [displayResults],
  );

  const virusTotalRows = useMemo(
    () => (displayResults || []).filter(isThreatIntelVTRow),
    [displayResults],
  );

  /** Cards actually rendered inside Breaches & Risk (matches DOM). */
  const breachesRiskCards = useMemo(
    () => [
      ...complianceRows,
      ...corporateBreachesRows,
      ...darkWebRows,
      ...domainSecurityRows,
      ...virusTotalRows,
    ],
    [
      complianceRows,
      corporateBreachesRows,
      darkWebRows,
      domainSecurityRows,
      virusTotalRows,
    ],
  );

  const breachesRiskCount = breachesRiskCards.length;

  const waybackRows = useMemo(
    () => (displayResults || []).filter(isWaybackRow),
    [displayResults],
  );

  const otherResults = useMemo(
    () =>
      (displayResults || []).filter(
        (r) =>
          !isNativeCorporateInfraRow(r) &&
          !isWhoisIntelRow(r) &&
          !isCertTransparencyRow(r) &&
          !isDomainSecurityAuditRow(r) &&
          !isThreatIntelVTRow(r) &&
          !isDarkWebAdvisoryRow(r) &&
          !isLegalRiskRow(r) &&
          !isDedicatedDdBucket(r) &&
          !isComplianceRow(r) &&
          !isCorporateBreachesRow(r) &&
          !isWaybackRow(r) &&
          r.ui_bucket !== 'corporate_context' &&
          r.ui_bucket !== 'executive_summary',
      ),
    [displayResults],
  );

  const contextResults = useMemo(
    () => (displayResults || []).filter((r) => r.ui_bucket === 'corporate_context'),
    [displayResults],
  );

  const branchLocationsFromContext = useMemo(() => {
    const locs = [];
    for (const row of contextResults || []) {
      const hints = row?.details?.location_hints;
      if (!Array.isArray(hints)) continue;
      for (const h of hints) {
        const t = String(h || '').trim();
        if (t && !locs.some((x) => x.toLowerCase() === t.toLowerCase())) locs.push(t);
      }
    }
    return locs;
  }, [contextResults]);

  const executiveSummaryRows = useMemo(
    () => (displayResults || []).filter((r) => r.ui_bucket === 'executive_summary'),
    [displayResults],
  );

  const keyPersonnel = useMemo(
    () => (displayResults || []).filter(isKeyPersonRow),
    [displayResults],
  );

  const uboNetwork = useMemo(
    () => (displayResults || []).filter(isUboRow),
    [displayResults],
  );

  const corporatePress = useMemo(
    () => (displayResults || []).filter(isCorporatePressRow),
    [displayResults],
  );

  const sentimentReviews = useMemo(
    () => (displayResults || []).filter(isSentimentRow),
    [displayResults],
  );

  const adverseMedia = useMemo(
    () => (displayResults || []).filter(isAdverseMediaRow),
    [displayResults],
  );

  const filterCounts = useMemo(() => {
    // Counts = cards effectively rendered for each category (not raw backend links).
    const infrastructure =
      nativeInfraRows.length + digitalFootprintOrdered.length + waybackRows.length;
    const legal = showLegalCourtsSection ? 1 : 0;
    const breachesRisk = breachesRiskCount;
    const peopleOnly = (keyPersonnel || []).filter(
      (r) =>
        String(r?.url || '').includes('/in/') &&
        String(r?.relation_type || '') !== 'LinkedIn People Search',
    );
    const peopleSearch = (keyPersonnel || []).filter(
      (r) =>
        String(r?.relation_type || '') === 'LinkedIn People Search' ||
        String(r?.details?.search_kind || '') === 'linkedin_people_directory',
    );
    const keyPersonsRendered =
      peopleOnly.length + peopleSearch.length + uboNetwork.length;
    const webPress = corporatePress.length + adverseMedia.length + otherResults.length;
    const reviews = sentimentReviews.length;
    const all =
      contextResults.length +
      executiveSummaryRows.length +
      infrastructure +
      legal +
      breachesRisk +
      keyPersonsRendered +
      webPress +
      reviews;
    return {
      all,
      infrastructure,
      legal,
      breaches_risk: breachesRisk,
      key_persons: keyPersonsRendered,
      web_press: webPress,
      reviews,
    };
  }, [
    nativeInfraRows,
    digitalFootprintOrdered,
    waybackRows,
    showLegalCourtsSection,
    breachesRiskCount,
    keyPersonnel,
    uboNetwork,
    corporatePress,
    adverseMedia,
    otherResults,
    sentimentReviews,
    contextResults,
    executiveSummaryRows,
  ]);

  const showDdIntelSections = loading || scanComplete;

  if (displayResults.length === 0 && loading) return null;

  return (
    <div className="min-w-0 max-w-full space-y-2.5 overflow-x-clip text-sm" data-testid="corporate-due-diligence-feed">
      <CorporateFeedFilters
        activeFilter={activeFilter}
        onFilterChange={handleFilterChange}
        counts={filterCounts}
      />

      {showSection(activeFilter, 'all') && contextResults.length > 0 && (
        <section
          className="overflow-hidden rounded-2xl border border-cyan-500/25 bg-cyan-950/15 shadow-sm"
          data-testid="corporate-search-context-section"
        >
          <div className="min-w-0 space-y-3 p-3 sm:p-4">
          {(() => {
            const primary = contextResults[0];
            const merged = {
              ...primary,
              details: {
                ...(primary?.details && typeof primary.details === 'object'
                  ? primary.details
                  : {}),
              },
            };
            const industrySet = [];
            const locationSet = [];
            for (const row of contextResults) {
              const inds = row?.details?.industry_display;
              if (Array.isArray(inds)) {
                for (const ind of inds) {
                  const t = String(ind || '').trim();
                  if (t && !industrySet.some((x) => x.toLowerCase() === t.toLowerCase())) {
                    industrySet.push(t);
                  }
                }
              }
              const locs = row?.details?.location_hints;
              if (Array.isArray(locs)) {
                for (const loc of locs) {
                  const t = String(loc || '').trim();
                  if (t && !locationSet.some((x) => x.toLowerCase() === t.toLowerCase())) {
                    locationSet.push(t);
                  }
                }
              }
              if (!merged.details.primary_brand && row?.details?.primary_brand) {
                merged.details.primary_brand = row.details.primary_brand;
              }
              if (!merged.details.person_focus && (row?.details?.person_focus || row?.person_focus)) {
                merged.details.person_focus =
                  row?.details?.person_focus || row?.person_focus;
              }
              if (!merged.person_focus && row?.person_focus) {
                merged.person_focus = row.person_focus;
              }
            }
            if (industrySet.length) merged.details.industry_display = industrySet;
            if (locationSet.length) merged.details.location_hints = locationSet;
            return renderCorporateFeedRow(
              merged,
              'corporate-context-canonical',
              getDisplayName,
              rowOpts,
            );
          })()}
          </div>
        </section>
      )}

      {showSection(activeFilter, 'all') && executiveSummaryRows.length > 0 && (
        <section className="min-w-0 space-y-2 overflow-x-clip rounded-2xl border border-slate-300/70 bg-slate-50/60 p-3 shadow-sm dark:border-slate-700/70 dark:bg-slate-950/40 sm:p-4">
          <div>
            <h3 className={DD_SECTION_TITLE.indigo}>Executive summary</h3>
            <p className={DD_SECTION_DESC}>
              Consolidated risk posture from AML, breaches, adverse media, and infrastructure signals.
            </p>
          </div>
          {executiveSummaryRows.map((res, i) =>
            renderCorporateFeedRow(res, `executive-summary-${i}`, getDisplayName, rowOpts),
          )}
        </section>
      )}

      {showSection(activeFilter, 'infrastructure') && hasDigitalFootprint && (
        <section
          id={CORP_SECTION_DOM_IDS.infrastructure}
          data-corp-section={CORP_SECTION_DOM_IDS.infrastructure}
          className="scroll-mt-20 min-w-0 space-y-3 overflow-x-clip rounded-2xl border border-indigo-200/80 bg-indigo-50/50 p-3 shadow-sm dark:border-indigo-800/60 dark:bg-indigo-950/35 sm:p-4"
        >
          <div>
            <h3 className={DD_SECTION_TITLE.indigo}>Digital footprint</h3>
            <p className={DD_SECTION_DESC}>
              Native infrastructure, WHOIS Intel, Cert Spotter, domain security, and VirusTotal
              — same layout as the records below.
            </p>
          </div>
          <EmailInfrastructureSection
            rows={nativeInfraRows}
            loading={loading}
            variant="corporate"
            onInspect={(row) => openInspect(row)}
          />
          <div className="space-y-3">
            {digitalFootprintOrdered.map((res, i) =>
              renderCorporateFeedRow(
                res,
                `digital-footprint-${i}-${res.ui_bucket || res.platform || 'row'}`,
                getDisplayName,
                rowOpts,
              ),
            )}
          </div>
        </section>
      )}

      {showSection(activeFilter, 'infrastructure') && waybackRows.length > 0 ? (
        <div className="space-y-3">
          {waybackRows.map((res, i) =>
            renderCorporateFeedRow(res, `wayback-${i}`, getDisplayName, rowOpts),
          )}
        </div>
      ) : null}

      {showSection(activeFilter, 'legal') ? (
        <div
          id={CORP_SECTION_DOM_IDS.legal}
          data-corp-section={CORP_SECTION_DOM_IDS.legal}
          className="scroll-mt-20 space-y-3"
        >
          {showLegalCourtsSection ? (
            <LegalCourtsCard
              records={legalRecords}
              loading={loading}
              scanComplete={scanComplete}
              companyName={companyName}
              companyDomain={companyDomain}
              onInspect={(row) => openInspect(row, { variant: 'legal_courts' })}
            />
          ) : null}
        </div>
      ) : null}

      {showSection(activeFilter, 'breaches_risk') &&
      (breachesRiskCount > 0 || loading || scanComplete) ? (
        <BreachesRiskSection
          displayResults={displayResults}
          loading={loading}
          cardCount={breachesRiskCount}
        >
          {complianceRows.map((res, i) =>
            renderCorporateFeedRow(res, `compliance-${i}`, getDisplayName, rowOpts),
          )}
          {corporateBreachesRows.map((res, i) =>
            renderCorporateFeedRow(res, `breaches-${i}`, getDisplayName, rowOpts),
          )}
          {darkWebRows.map((res, i) =>
            renderCorporateFeedRow(res, `darkweb-${i}`, getDisplayName, rowOpts),
          )}
          {domainSecurityRows.map((res, i) =>
            renderCorporateFeedRow(res, `domain-security-${i}`, getDisplayName, rowOpts),
          )}
          {virusTotalRows.map((res, i) =>
            renderCorporateFeedRow(res, `vt-${i}`, getDisplayName, rowOpts),
          )}
        </BreachesRiskSection>
      ) : null}

      {showDdIntelSections ? (
        <>
          {showSection(activeFilter, 'key_persons') && uboNetwork.length > 0 ? (
            <CorporateDdSection
              sectionId={
                keyPersonnel.length === 0 ? CORP_SECTION_DOM_IDS.key_persons : undefined
              }
              title="UBO network"
              description="Ownership, partnership, and corporate connection mentions from open web sources."
              borderClass="border-sky-200/80 bg-sky-50/50 dark:border-sky-800/60 dark:bg-sky-950/35"
              titleClass={DD_SECTION_TITLE.sky}
              hasRows
              loading={loading}
              scanComplete={scanComplete}
              hideWhenEmpty
            >
              {uboNetwork.map((res, i) =>
                renderCorporateFeedRow(res, `ubo-${i}`, getDisplayName, rowOpts),
              )}
            </CorporateDdSection>
          ) : null}

          {showSection(activeFilter, 'web_press') && corporatePress.length > 0 ? (
            <CorporateDdSection
              sectionId={CORP_SECTION_DOM_IDS.web_press}
              title="Corporate press"
              description="Industry press, community mentions, and non-adverse brand footprint."
              borderClass="border-emerald-200/80 bg-emerald-50/50 dark:border-emerald-800/60 dark:bg-emerald-950/35"
              titleClass={DD_SECTION_TITLE.emerald}
              hasRows
              loading={loading}
              scanComplete={scanComplete}
              hideWhenEmpty
            >
              {corporatePress.map((res, i) =>
                renderCorporateFeedRow(res, `press-${i}`, getDisplayName, rowOpts),
              )}
            </CorporateDdSection>
          ) : null}

          {showSection(activeFilter, 'reviews') && sentimentReviews.length > 0 ? (
            <CorporateDdSection
              sectionId={CORP_SECTION_DOM_IDS.reviews}
              title="Reviews & sentiment"
              description="Glassdoor, Trustpilot, and UndeLucram signals about workplace culture and reputation."
              borderClass="border-violet-200/80 bg-violet-50/50 dark:border-violet-800/60 dark:bg-violet-950/35"
              titleClass={DD_SECTION_TITLE.violet}
              hasRows
              loading={loading}
              scanComplete={scanComplete}
              hideWhenEmpty
            >
              {sentimentReviews.map((res, i) =>
                renderCorporateFeedRow(res, `sentiment-${i}`, getDisplayName, rowOpts),
              )}
            </CorporateDdSection>
          ) : null}

          {showSection(activeFilter, 'web_press') && adverseMedia.length > 0 ? (
            <CorporateDdSection
              sectionId={
                corporatePress.length === 0 ? CORP_SECTION_DOM_IDS.web_press : undefined
              }
              title="Adverse media"
              description="Negative news, litigation context, and reputational risk articles."
              borderClass="border-rose-200/80 bg-rose-50/50 dark:border-rose-800/60 dark:bg-rose-950/35"
              titleClass={DD_SECTION_TITLE.rose}
              hasRows
              loading={loading}
              scanComplete={scanComplete}
              hideWhenEmpty
            >
              {adverseMedia.map((res, i) =>
                renderCorporateFeedRow(res, `adverse-${i}`, getDisplayName, rowOpts),
              )}
            </CorporateDdSection>
          ) : null}
        </>
      ) : null}

      {showSection(activeFilter, 'web_press')
        ? otherResults.map((res, idx) => renderCorporateFeedRow(res, idx, getDisplayName, rowOpts))
        : null}

      {showDdIntelSections &&
      showSection(activeFilter, 'key_persons') &&
      (keyPersonnel.length > 0 || companyName || companyDomain) ? (
        <CorporateDdSection
          sectionId={CORP_SECTION_DOM_IDS.key_persons}
          title="Key persons"
          description="LinkedIn profiles and leadership signals mapped to the target company."
          borderClass="border-teal-200/80 bg-teal-50/50 dark:border-teal-800/60 dark:bg-teal-950/35"
          titleClass={DD_SECTION_TITLE.teal}
          hasRows
          loading={loading}
          scanComplete={scanComplete}
          hideWhenEmpty={false}
        >
          <KeyPersonsSection
            rows={keyPersonnel}
            onPivotToPerson={onPivotToPerson}
            companyName={companyName}
            companyDomain={companyDomain}
            locationTags={locationTags}
            branchLocations={branchLocationsFromContext}
          />
        </CorporateDdSection>
      ) : null}
    </div>
  );
};

export default CorporateDueDiligence;
